import type { CharDisplayMode } from './types'
export type { CharDisplayMode } from './types'

type AssertTrue<T extends true> = T

/**
 * Default display modes advertised by Char hosts.
 *
 * Order matters for fallback behavior. When a preferred mode is unavailable,
 * the first available mode is selected.
 *
 * @public
 */
export const DEFAULT_AVAILABLE_DISPLAY_MODES = [
	'inline',
	'fullscreen',
	'pip',
] as const satisfies readonly CharDisplayMode[]

/**
 * Allowed display mode values for Char host policy resolution.
 */
type DisplayModesFromDefaults = (typeof DEFAULT_AVAILABLE_DISPLAY_MODES)[number]

type _AssertDisplayModesStayInSync = AssertTrue<
	[DisplayModesFromDefaults] extends [CharDisplayMode]
		? [CharDisplayMode] extends [DisplayModesFromDefaults]
			? true
			: false
		: false
>

/**
 * Runtime guard for Char display mode values.
 *
 * @param value - Raw value from host attributes, events, or external input.
 * @returns `true` when the value is one of `inline`, `fullscreen`, or `pip`.
 *
 * @public
 */
export function isDisplayMode(
	value: string | null | undefined,
): value is CharDisplayMode {
	return value === 'inline' || value === 'fullscreen' || value === 'pip'
}

/**
 * Checks whether a display mode represents an expanded agent surface.
 *
 * Expanded modes are:
 * - `inline` (panel-style)
 * - `fullscreen` (viewport-filling)
 *
 * Collapsed mode is:
 * - `pip` (launcher-only)
 *
 * @param mode - Current display mode from host context.
 * @returns `true` for expanded modes and `false` for collapsed/undefined modes.
 *
 * @public
 */
export function isExpandedDisplayMode(mode: CharDisplayMode | undefined): boolean {
	return mode === 'inline' || mode === 'fullscreen'
}

/**
 * Resolves a preferred display mode against host-supported modes.
 *
 * An empty `availableModes` array means "no constraint" — the preferred mode
 * is returned unconditionally. This is intentional: it allows callers to opt
 * out of mode filtering without a separate code path.
 *
 * @param preferred - Preferred mode requested by host policy.
 * @param availableModes - Supported modes advertised by the host.
 * @returns Preferred mode when supported, otherwise the first available mode.
 *
 * @public
 */
export function resolveSupportedDisplayMode(
	preferred: CharDisplayMode,
	availableModes: readonly CharDisplayMode[] = DEFAULT_AVAILABLE_DISPLAY_MODES,
): CharDisplayMode {
	if (availableModes.length === 0) {
		return preferred
	}

	if (availableModes.includes(preferred)) {
		return preferred
	}

	console.debug(`[Char] Display mode "${preferred}" not in available modes [${availableModes.join(', ')}], using "${availableModes[0]}"`)
	return availableModes[0]
}

/**
 * Resolves Char's standard host display-mode policy.
 *
 * Policy:
 * - Closed state to `pip`
 * - Open on desktop to `inline`
 * - Open on narrow viewport to `fullscreen`
 *
 * The resolved mode is then constrained to `availableModes`.
 *
 * @param options - Host orchestration inputs: `open`, `isNarrowViewport`,
 * and optional `availableModes`.
 * @returns Final mode that satisfies policy and availability constraints.
 *
 * @public
 */
export function resolvePolicyDisplayMode(options: {
	open: boolean
	isNarrowViewport: boolean
	availableModes?: readonly CharDisplayMode[]
}): CharDisplayMode {
	const availableModes = options.availableModes ?? DEFAULT_AVAILABLE_DISPLAY_MODES
	const preferred: CharDisplayMode = options.open
		? options.isNarrowViewport
			? 'fullscreen'
			: 'inline'
		: 'pip'

	return resolveSupportedDisplayMode(preferred, availableModes)
}
